import * as Sentry from '@sentry/react';
import { apiClient, FetchError, fetchWithError, HTTPError } from 'shared/api-client';
import { API } from 'shared/config';
import type { LoanApplicationId, WebRecId } from 'shared/schemas/common';
import { getTokens } from 'shared/tokens';
import { cleanParams } from 'shared/utils';

import { isAnalyticsDebugEnabled } from './debug.ts';
import { createEventQueue, type EventQueue, type SendOptions } from './event-queue.ts';
import { getGlobalAnalyticsParams } from './global-params.ts';
import { addGTMValue } from './gtm.ts';

type AnalyticsEventKind =
  | { origin_kind: 'LIVE_OFFERS'; action_name: `so_${string}` }
  | { origin_kind: 'USER_PORTAL'; action_name: `up_${string}` };

type AnalyticsEvent = AnalyticsEventKind & {
  action_category?: string;
  action_label: string;
  extra_parameters?: {
    web_rec_id?: WebRecId;
    loan_offer_ada_id?: LoanApplicationId;
  } & Record<string, any>;
};

const serializeEvent = (payload: AnalyticsEvent) => {
  const { web_rec_id, loan_offer_ada_id, ...rest } = payload.extra_parameters ?? {};
  const params = cleanParams(rest);
  const hasParams = Object.keys(params).length > 0;

  return {
    ...payload,
    web_rec_id,
    loan_offer_ada_id,
    extra_parameters: hasParams ? rest : undefined,
    timestamp: new Date().toISOString(),
  };
};

type SerializedEvent = ReturnType<typeof serializeEvent> & { deduplication_id: string };

const sendApiEvent = (payload: AnalyticsEvent) => {
  return apiClient.post('eventing/system_events/record/by_user/', {
    json: serializeEvent(payload),
    keepalive: true,
  });
};

const BATCH_PATH = 'eventing/system_events/record/batch/by_user/';

class BatchResponseError extends Error {
  name = 'BatchResponseError';
  readonly status: number;
  constructor(status: number) {
    super(`Batch request failed with status ${status}`);
    this.status = status;
  }
}

const getErrorStatus = (error: unknown) => {
  if (error instanceof HTTPError) return error.response.status;
  if (error instanceof BatchResponseError) return error.status;
  return undefined;
};

/** Network errors, expired tokens (refreshed on the next regular flush) and server errors are worth retrying. */
const isRetryableError = (error: unknown) => {
  const status = getErrorStatus(error);
  if (status === undefined) return error instanceof FetchError;
  return status >= 500 || status === 401 || status === 408 || status === 429;
};

const analyticsLogStyles = `
  color: white;
  background-color: #368f5b;
  padding: 2px 5px;
  border-radius: 3px;
  font-weight: bold;
`;

const sendBatch = async (events: SerializedEvent[], { unloading }: SendOptions) => {
  if (isAnalyticsDebugEnabled()) {
    console.groupCollapsed(
      '%cAnalytics batch',
      analyticsLogStyles,
      `${events.length} event${events.length === 1 ? '' : 's'}`,
      unloading ? '(unloading)' : '',
    );
    console.log({ unloading, events });
    console.groupEnd();
  }

  if (!unloading) {
    await apiClient.post(BATCH_PATH, { json: { events }, keepalive: true });
    return;
  }

  // apiClient awaits token operations before sending, the page might be gone by then
  const tokens = getTokens();
  const response = await fetchWithError(`${API.url}/${BATCH_PATH}`, {
    method: 'POST',
    keepalive: true,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(tokens && { authorization: `Bearer ${tokens.accessToken}` }),
    },
    body: JSON.stringify({ events }),
  });
  if (!response.ok) throw new BatchResponseError(response.status);
};

export type AnalyticsBatchingConfig = {
  enabled: boolean;
  flushIntervalMs: number;
  maxBatchSize: number;
};

let batchingConfig: AnalyticsBatchingConfig | undefined;
let eventQueue: EventQueue<SerializedEvent> | undefined;

/** Idempotent, safe to call on every render. */
export const configureAnalyticsBatching = (config: AnalyticsBatchingConfig) => {
  if (
    batchingConfig?.enabled === config.enabled &&
    batchingConfig.flushIntervalMs === config.flushIntervalMs &&
    batchingConfig.maxBatchSize === config.maxBatchSize
  ) {
    return;
  }
  batchingConfig = config;

  void eventQueue?.dispose();
  eventQueue = config.enabled
    ? createEventQueue<SerializedEvent>({
        flushIntervalMs: config.flushIntervalMs,
        maxBatchSize: config.maxBatchSize,
        maxBatchBytes: 32_000,
        maxAttempts: 3,
        send: sendBatch,
        isRetryable: isRetryableError,
        onDrop: (events, error) => {
          Sentry.captureException(error, {
            tags: { analytics: 'batch-dropped' },
            extra: { count: events.length, actions: events.map((e) => e.action_name) },
          });
        },
      })
    : undefined;
};

const sendBatchedApiEvent = (payload: AnalyticsEvent) => {
  if (!eventQueue) return sendApiEvent(payload);
  eventQueue.enqueue({ ...serializeEvent(payload), deduplication_id: crypto.randomUUID() });
};

/**
 * Sends queued events, use before hard redirects (`window.location` changes) so they aren't lost.
 * Requests use `keepalive` so navigating away after the timeout doesn't cancel them.
 */
export const flushAnalyticsEvents = ({ timeoutMs = 1000 }: { timeoutMs?: number } = {}) => {
  if (!eventQueue) return Promise.resolve();
  return Promise.race([
    eventQueue.flush(),
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
};

const sendGTMEvent = (event: AnalyticsEvent) => {
  addGTMValue({
    ...event.extra_parameters,
    category: event.action_category,
    event: event.action_name,
    action: event.action_name,
  });
};

export const sendAnalyticsEvent = (event: AnalyticsEvent) => {
  const globalParams = getGlobalAnalyticsParams();

  const enhancedEvent = {
    ...event,
    action_category: event.action_category || 'tracking',
    extra_parameters: {
      ...globalParams,
      ...event.extra_parameters,
    },
  };

  if (isAnalyticsDebugEnabled()) {
    console.groupCollapsed('%cAnalytics', analyticsLogStyles, enhancedEvent.action_name);
    console.log(enhancedEvent);
    console.groupEnd();
  }

  void sendBatchedApiEvent(enhancedEvent);
  // Temporary backwards compatibility: GTM keeps the historical event name, without the labs up_ prefix.
  if (enhancedEvent.origin_kind === 'USER_PORTAL') {
    const getActionName = (event: string) => {
      // Special case, don't override the native GTM page view event.
      if (event === 'up_page_view') return 'up_page_view';
      return event.replace(/^up_/, '') as `up_${string}`;
    };

    sendGTMEvent({
      ...enhancedEvent,
      action_name: getActionName(enhancedEvent.action_name),
    });
  } else {
    sendGTMEvent(enhancedEvent);
  }
};

export const sendUserPortalEvent = (event: string, extra_parameters?: Record<string, any>) => {
  if (event === undefined) {
    const error = new Error('sendUserPortalEvent: event is undefined');
    console.warn(error);
    Sentry.captureException(error, { extra: { extra_parameters } });
  }
  return sendAnalyticsEvent({
    action_name: `up_${event}`,
    action_label: 'user_portal',
    origin_kind: 'USER_PORTAL',
    action_category: extra_parameters?.category || extra_parameters?.action_category,
    extra_parameters: {
      ...extra_parameters,
    },
  });
};
